/**
 * Auth Slice
 *
 * Hybrid pattern: React Query fetches, Zustand stores derived state.
 */

import type { StateCreator } from 'zustand';
import type { BoundStoreState, AuthSlice } from '../types';
import type { User } from '@/features/auth/types';
import type { AccountStatus } from '@/features/auth/types';

// ============================================================================
// Initial State
// ============================================================================

export const authSliceInitialState = {
  user: null as User | null,
  isAuthenticated: false,
  isAuthLoading: true,
  accountStatus: null as AccountStatus | null,
  forceLogout: false, // Flag for API interceptor to trigger React-friendly logout
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createAuthSlice: StateCreator<
  BoundStoreState,
  [['zustand/devtools', never]],
  [],
  AuthSlice
> = (set) => ({
  // State
  ...authSliceInitialState,

  // Actions
  setAuth: (state) => {
    return set(
      (current) => ({
        user: state.user !== undefined ? state.user : current.user,
        isAuthenticated: state.isAuthenticated !== undefined ? state.isAuthenticated : (state.user !== undefined ? !!state.user : current.isAuthenticated),
        isAuthLoading: state.isAuthLoading !== undefined ? state.isAuthLoading : current.isAuthLoading,
        accountStatus: state.accountStatus !== undefined ? state.accountStatus : (state.user !== undefined ? state.user?.status ?? current.accountStatus : current.accountStatus),
      }),
      undefined,
      'auth/setAuth'
    );
  },

  clearAuth: () => {
    return set(
      {
        ...authSliceInitialState,
        isAuthLoading: false, // Ensure we don't get stuck in loading state after logout
      },
      undefined,
      'auth/clear'
    );
  },

  triggerForceLogout: () => {
    return set({ forceLogout: true }, undefined, 'auth/forceLogout');
  },

  resetForceLogout: () => {
    return set({ forceLogout: false }, undefined, 'auth/resetForceLogout');
  },
});

